import React from "react";
import { css } from "@emotion/react";
import {
  createShouldForwardProp,
  props,
} from "@styled-system/should-forward-prop";
import {
  Box,
  Label,
  BoxProps,
  themeGet,
  useTheme,
  LoadingSpinner,
} from "../../";
import { styled, EmotionComponent } from "../../styled";
import { useUID } from "../../hooks";
import {
  FocusBorderColorProp,
  focusBorderColorProp,
  space,
  color,
  typography,
  border,
  display,
  layout,
  compose,
  SpaceProps,
  ColorProps,
  TypographyProps,
  BorderProps,
  DisplayProps,
  LayoutProps,
  system,
  OverrideNativeElementProps,
  placeholderTextProps,
  PlaceholderTextProps,
} from "../../utils/styleFn";

const hoverColorTransform = system({
  hoverBorderColor: {
    property: "borderColor",
    scale: "colors",
  },
});

const activeTextColorTransform = system({
  activeTextColor: {
    property: "color",
    scale: "colors",
  },
});

const customStyles = compose(space, color, typography, border, display, layout);

type InputRawProps = {
  /** container props */
  containerProps?: BoxProps;
  /** the text for the floating label*/
  floatingLabelText?: string;
  /** border color when the input get hovered */
  hoverBorderColor?: string | string[];
  /**textColor when active */
  activeTextColor?: string | string[];
  /** is Required or not */
  required?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input is in error state */
  error?: boolean;
  /** Switch the underline input */
  underline?: boolean;
  /** Whether the input is read-only */
  readOnly?: boolean;
  /** Adds a loading spinner for the input */
  loading?: boolean;
} & FocusBorderColorProp &
  PlaceholderTextProps &
  SpaceProps &
  ColorProps &
  TypographyProps &
  BorderProps &
  DisplayProps &
  LayoutProps;

export type InputProps = OverrideNativeElementProps<InputRawProps, "input">;

const shouldForwardProp = createShouldForwardProp([
  ...props.filter((prop) => prop != "size"),
]);

const InputLoading = ({ disabled }: { disabled: boolean }) => (
  <LoadingSpinner
    position="absolute"
    right="10px"
    top="50%"
    mt="-10px"
    ringSize="2px"
    ringBg={disabled ? "white" : "ui.backgroundColor"}
    width="20px"
    height="20px"
  />
);

const BasicInput: EmotionComponent<InputProps> = styled("input", {
  shouldForwardProp,
})<InputProps>`
  outline: 0;
  appearance: none;
  transition: border-color ${themeGet("transitions.default")};

  ${customStyles}

  ::placeholder {
    font-weight: ${themeGet("fontWeights.normal")};
    color: ${themeGet("colors.typography.inactive")};
    ${placeholderTextProps}
  }

  ::-ms-input-placeholder {
    font-weight: ${themeGet("fontWeights.normal")};
    color: ${themeGet("colors.typography.inactive")};
    ${placeholderTextProps}
  }

  &:focus,
  &:hover:not(:disabled) {
    ${activeTextColorTransform}
  }

  ${(props) =>
    props.floatingLabelText &&
    props.floatingLabelText.length > 0 &&
    `
    + label {
      cursor: text;
      white-space: nowrap;
      text-overflow: ellipsis;
      transition: all 0.2s ease;
      transform: translateY(-50%);
      pointer-events: none;
    }

    ::placeholder {
      opacity: 0;
    }

    ${
      props.readOnly
        ? `:placeholder-shown + label,`
        : `:placeholder-shown + label {
      top: 50%;
      color: ${themeGet("colors.typography.inactive")};
    }`
    }
    :not(:placeholder-shown) + label,
    :focus + label {
      top: 0;
      left: 0.5rem;
      font-size: 0.75rem;
      pointer-events: none;
    }

    :not(:placeholder-shown):not(:disabled) + label {
      color: ${props.error ? themeGet("colors.ui.error")(props) : "inherit"};
    }
    `}

  ${(props) =>
    !props.error &&
    !props.disabled &&
    css`
      :focus {
        ${focusBorderColorProp(props)}
      }

      :hover {
        ${hoverColorTransform(props)}
      }

      :placeholder-shown:hover + label {
        color: ${themeGet("colors.secondaryDark")(props)};
      }

      :focus + label {
        color: ${themeGet("colors.secondary")(props)};
      }
    `};

  :disabled {
    cursor: not-allowed;
    color: ${themeGet("colors.typography.inactive")};
    background-color: ${themeGet("colors.ui.backgroundColor")};
  }

  ::-ms-clear {
    display: none;
  }

  ${(props) =>
    props.underline &&
    css`
      border: none;
      border-bottom: ${themeGet("borders.2")(props)};
      border-color: ${themeGet("colors.ui.borderColor")(props)};
      border-radius: 0;
      padding-bottom: 0.625rem;
    `}

  ${(props) =>
    props.error &&
    css`
      border-color: ${themeGet("colors.ui.error")(props)};
      :not(:placeholder-shown) + label {
        color: ${themeGet("colors.ui.error")(props)};
      }
    `};
`;

export const Input: React.FC<InputProps> = React.forwardRef(
  (
    {
      containerProps,
      floatingLabelText = "",
      required,
      disabled = false,
      error,
      readOnly,
      id,
      loading,
      ...otherProps
    },
    ref
  ) => {
    const theme = useTheme();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const basicInputId = id || useUID();

    return floatingLabelText.length > 0 ? (
      <Box position="relative" {...containerProps}>
        <BasicInput
          id={basicInputId}
          ref={ref}
          required={required}
          aria-required={required}
          disabled={disabled}
          error={error}
          readOnly={readOnly}
          placeholder={floatingLabelText}
          floatingLabelText={floatingLabelText}
          {...otherProps}
        />
        <Label
          htmlFor={basicInputId}
          maxWidth="66.6%"
          overflow="hidden"
          width="auto"
          position="absolute"
          left="1.25rem"
          px="3px"
          mb="0"
          required={required}
          color={
            disabled
              ? theme?.colors?.typography?.inactive
              : error
              ? theme?.colors?.["ui"]?.["error"] || "red"
              : "inherit"
          }
          bg={!disabled ? "white" : theme?.colors?.["ui"]?.["backgroundColor"]}
        >
          {floatingLabelText}
        </Label>
        {loading && <InputLoading disabled={disabled} />}
      </Box>
    ) : (
      <BasicInput
        id={basicInputId}
        ref={ref}
        aria-required={required}
        required={required}
        disabled={disabled}
        error={error}
        readOnly={readOnly}
        {...otherProps}
      />
    );
  }
);

Input.displayName = "Input";

Input.defaultProps = {
  p: 5,
  bg: "white",
  display: "block",
  width: "100%",
  color: "typography.text",
  fontSize: "base",
  lineHeight: "normal",
  border: 1,
  borderRadius: "default",
  borderColor: "ui.borderColor",
  textAlign: "left",
  hoverBorderColor: "secondaryDark",
  focusBorderColor: "secondary",
};

export default Input;
